import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { GraduationCap, Lock, User, Mail, ArrowLeft, RefreshCw, Shield, BookOpen, Users, Award, KeyRound, CheckCircle } from 'lucide-react';

// Forgot Password Component
const ForgotPasswordForm = ({ onBack, API_URL }) => {
  const [forgotStep, setForgotStep] = useState('email'); // 'email', 'otp', 'newPassword', 'success'
  const [email, setEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSessionId, setResetSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const startCountdown = () => {
    setCountdown(30);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendResetOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email: email.trim()
      });
      setResetSessionId(response.data.session_id);
      setForgotStep('otp');
      startCountdown();
      toast.success('Reset code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetOTP = async (e) => {
    e.preventDefault();
    if (!resetOtp.trim() || resetOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/verify-reset-otp`, {
        email: email.trim(),
        otp: resetOtp.trim(),
        session_id: resetSessionId
      });
      setForgotStep('newPassword');
      toast.success('Code verified! Please set your new password');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        email: email.trim(),
        session_id: resetSessionId,
        new_password: newPassword
      });
      setForgotStep('success');
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email: email.trim()
      });
      setResetSessionId(response.data.session_id);
      startCountdown();
      toast.success('Reset code resent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  if (forgotStep === 'success') {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Password Reset Successful!</h3>
          <p className="text-slate-500">Your password has been changed. You can now login with your new password.</p>
        </div>
        <Button
          onClick={onBack}
          className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-base font-semibold rounded-xl"
          data-testid="back-to-login-button"
        >
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to login
      </button>

      <div className="text-center lg:text-left mb-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          {forgotStep === 'email' ? 'Forgot Password?' : 
           forgotStep === 'otp' ? 'Verify Email' : 'Set New Password'}
        </h2>
        <p className="text-slate-500 text-sm">
          {forgotStep === 'email' ? 'Enter your registered email to receive a reset code' :
           forgotStep === 'otp' ? 'Enter the 6-digit code sent to your email' :
           'Create a strong new password'}
        </p>
      </div>

      {forgotStep === 'email' && (
        <form onSubmit={handleSendResetOTP} className="space-y-5">
          <div>
            <Label htmlFor="reset-email" className="text-slate-700 font-medium text-sm">
              Email Address
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 h-12 text-base border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                placeholder="Enter your registered email"
                data-testid="reset-email-input"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-base font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300"
            disabled={loading}
            data-testid="send-reset-code-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" />
                Sending...
              </span>
            ) : (
              'Send Reset Code'
            )}
          </Button>
        </form>
      )}

      {forgotStep === 'otp' && (
        <form onSubmit={handleVerifyResetOTP} className="space-y-5">
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-4 text-center">
            <Mail className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-sm text-slate-600">Reset code sent to</p>
            <p className="text-base font-semibold text-slate-900">{email}</p>
          </div>

          <div>
            <Label htmlFor="reset-otp" className="text-slate-700 font-medium text-sm">
              Reset Code
            </Label>
            <Input
              id="reset-otp"
              type="text"
              value={resetOtp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setResetOtp(value);
              }}
              className="text-center text-2xl tracking-[0.5em] font-mono mt-2 h-14 border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
              placeholder="------"
              maxLength={6}
              data-testid="reset-otp-input"
              required
            />
            <p className="text-xs text-slate-500 mt-2 text-center">Code expires in 10 minutes</p>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-base font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300"
            disabled={loading || resetOtp.length !== 6}
            data-testid="verify-reset-otp-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" />
                Verifying...
              </span>
            ) : (
              'Verify Code'
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={countdown > 0 || loading}
              className={`text-sm transition-colors ${countdown > 0 ? 'text-slate-400' : 'text-purple-600 hover:text-purple-700 font-medium'}`}
            >
              {countdown > 0 ? `Resend code in ${countdown}s` : "Didn't receive code? Resend"}
            </button>
          </div>
        </form>
      )}

      {forgotStep === 'newPassword' && (
        <form onSubmit={handleResetPassword} className="space-y-5">
          <div>
            <Label htmlFor="new-password" className="text-slate-700 font-medium text-sm">
              New Password
            </Label>
            <div className="relative mt-2">
              <KeyRound className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-12 h-12 text-base border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password-input"
                required
                minLength={6}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="confirm-password" className="text-slate-700 font-medium text-sm">
              Confirm Password
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-12 h-12 text-base border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-base font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300"
            disabled={loading}
            data-testid="reset-password-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin" />
                Resetting...
              </span>
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>
      )}
    </div>
  );
};

export default function Login() {
  const [step, setStep] = useState('credentials'); // 'credentials', 'otp', 'forgot'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const startCountdown = () => {
    setCountdown(30);
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      // First check if user requires OTP or can login directly
      const checkResponse = await axios.post(`${API_URL}/auth/check-login`, {
        username: username.trim(),
        password: password
      });
      
      if (checkResponse.data.requires_otp) {
        // Admin/Sub Admin/Master Agency - need OTP verification
        setSessionId(checkResponse.data.session_id);
        setEmailHint(checkResponse.data.email_hint);
        setStep('otp');
        startCountdown();
        toast.success('OTP sent to your email');
      } else {
        // Agency user - direct login
        const loginResponse = await axios.post(`${API_URL}/auth/agency-login`, {
          username: username.trim(),
          password: password
        });
        
        const result = login(loginResponse.data);
        if (result.success) {
          toast.success('Login successful!');
          navigate('/', { replace: true });
        } else {
          toast.error('Failed to save login session');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-otp`, {
        username: username.trim(),
        otp: otp.trim(),
        session_id: sessionId
      });
      
      const result = login(response.data);
      
      if (result.success) {
        toast.success('Login successful!');
        navigate('/', { replace: true });
      } else {
        toast.error('Failed to save login session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setResending(true);
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('username', username);
      
      const response = await axios.post(`${API_URL}/auth/resend-otp`, formData);
      setEmailHint(response.data.email_hint);
      startCountdown();
      toast.success('OTP resent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('credentials');
    setOtp('');
    setSessionId('');
    setEmailHint('');
    setCountdown(0);
  };

  const features = [
    { icon: BookOpen, text: 'Streamlined Admissions' },
    { icon: Users, text: 'Agency Management' },
    { icon: Shield, text: 'Secure & Compliant' },
    { icon: Award, text: 'Track Progress' }
  ];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Hero Section (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-gradient-to-br from-[#0f0a1a] via-[#1a1030] to-[#0f0a1a] overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-indigo-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 py-12">
          {/* Logo & Title */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-lg shadow-purple-500/30">
                <GraduationCap className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl xl:text-4xl font-bold text-white tracking-tight">
                  College Sarathi
                </h1>
                <p className="text-purple-300/80 text-sm">Admission Management ERP</p>
              </div>
            </div>
          </div>

          {/* Hero Text */}
          <div className="space-y-6 mb-12">
            <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Simplify Your
              <span className="block bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Admission Process
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-lg leading-relaxed">
              Powerful B2B admission management platform for agencies and counselors. 
              Manage applications, track payments, and grow your business.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
                >
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Icon className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-sm font-medium text-slate-300">{feature.text}</span>
                </div>
              );
            })}
          </div>

          {/* Stats */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex gap-12">
              <div>
                <p className="text-3xl font-bold text-white">80000+</p>
                <p className="text-sm text-slate-400">Applications Processed</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">1000+</p>
                <p className="text-sm text-slate-400">Partner Agencies</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">50+</p>
                <p className="text-sm text-slate-400">Universities</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-12 bg-slate-50 lg:bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo (visible only on mobile) */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-bold text-slate-900">College Sarathi</h1>
                <p className="text-sm text-slate-500">Admission Management ERP</p>
              </div>
            </div>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl lg:shadow-none p-6 sm:p-8 border border-slate-200 lg:border-0">
            {step !== 'forgot' && (
              <div className="mb-8 text-center lg:text-left">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {step === 'credentials' ? 'Welcome back' : 'Verify OTP'}
                </h2>
                <p className="text-slate-500">
                  {step === 'credentials' 
                    ? 'Sign in to your account to continue' 
                    : 'Enter the verification code sent to your email'}
                </p>
              </div>
            )}

            {step === 'credentials' ? (
              <form onSubmit={handleRequestOTP} className="space-y-5">
                <div>
                  <Label htmlFor="username" className="text-slate-700 font-medium text-sm">
                    Username
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="pl-12 h-12 text-base border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                      placeholder="Enter your username"
                      data-testid="username-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-700 font-medium text-sm">
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-12 h-12 text-base border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                      placeholder="Enter your password"
                      data-testid="password-input"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-base font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Please wait...
                    </span>
                  ) : (
                    'LOGIN'
                  )}
                </Button>

                {/* Forgot Password Link */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('forgot')}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              </form>
            ) : step === 'forgot' ? (
              <ForgotPasswordForm 
                onBack={() => setStep('credentials')} 
                API_URL={API_URL}
              />
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-5">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm mb-4 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>

                <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <p className="text-sm text-slate-600">
                    We've sent a 6-digit code to
                  </p>
                  <p className="text-lg font-semibold text-slate-900 mt-1">{emailHint}</p>
                </div>

                <div>
                  <Label htmlFor="otp" className="text-slate-700 font-medium text-sm">
                    Verification Code
                  </Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(value);
                    }}
                    className="text-center text-2xl tracking-[0.5em] font-mono mt-2 h-14 border-slate-200 focus:border-purple-500 focus:ring-purple-500 rounded-xl"
                    placeholder="------"
                    maxLength={6}
                    data-testid="otp-input"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Code expires in 5 minutes
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-base font-semibold rounded-xl shadow-lg shadow-purple-500/30 transition-all duration-300"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Sign In'
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || resending}
                    className={`text-sm transition-colors ${countdown > 0 ? 'text-slate-400' : 'text-purple-600 hover:text-purple-700 font-medium'}`}
                  >
                    {resending ? (
                      <span className="flex items-center gap-1 justify-center">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Resending...
                      </span>
                    ) : countdown > 0 ? (
                      `Resend code in ${countdown}s`
                    ) : (
                      "Didn't receive code? Resend"
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Divider */}
            <div className="relative my-6 sm:my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">Need Help?</span>
              </div>
            </div>

            {/* Help Section - Compact single row */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm">
                <a 
                  href="tel:+918595747197" 
                  className="text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                >
                  +91 8595 747197
                </a>
                <span className="text-slate-300">|</span>
                <a 
                  href="mailto:mail@collegesarathi.in" 
                  className="text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                >
                  mail@collegesarathi.in
                </a>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-4 sm:mt-6">
            © {new Date().getFullYear()} College Sarathi. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
